import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';
import Header from '../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SurnameWiseVoters = () => {
    // --- State ---
    const [voters, setVoters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterName, setFilterName] = useState("");

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // --- Context & Router ---
    const { appId, language } = useAppId();
    const location = useLocation();
    const navigate = useNavigate();

    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            // Page titles
            title: "Surname Search Results",
            subtitlePrefix: "Showing voters for:",

            // Search
            searchPlaceholder: "Filter displayed results by name...",

            // Messages
            loading: "Fetching Voters...",
            noVotersFound: "No voters found.",
            noMatchingNames: "No matching names found.",
            mobileNotAvailable: "Mobile number not available for this voter.",
            fetchFamilyFailed: "Could not fetch family details.",
            messageError: "Error generating message.",

            // Labels
            fatherHusband: "F/H :",
            address: "Address :",
            serialNumber: "Serial No. :",
            mobile: "Mobile :",
            idCard: "ID Card :",
            boothNo: "Booth No. :",
            houseNo: "House No. :",
            pollingLocation: "Polling Location :",
            n_a: "N/A",

            // Action buttons
            call: "Call",
            text: "Text",
            slip: "Slip",
            check: "Check",
            prachar: "Prachar",
            print: "Print",
            family: "Family"
        },

        hi: {
            // Page titles
            title: "उपनाम खोज परिणाम",
            subtitlePrefix: "के लिए मतदाता दिखा रहा है:",

            // Search
            searchPlaceholder: "प्रदर्शित परिणामों को नाम से फ़िल्टर करें...",

            // Messages
            loading: "मतदाता लाए जा रहे हैं...",
            noVotersFound: "कोई मतदाता नहीं मिला।",
            noMatchingNames: "कोई मिलान नाम नहीं मिला।",
            mobileNotAvailable: "इस मतदाता के लिए मोबाइल नंबर उपलब्ध नहीं है।",
            fetchFamilyFailed: "परिवार विवरण प्राप्त नहीं किया जा सका।",
            messageError: "संदेश बनाने में त्रुटि।",

            // Labels
            fatherHusband: "पिता/पति :",
            address: "पता :",
            serialNumber: "क्रमांक :",
            mobile: "मोबाइल :",
            idCard: "पहचान पत्र :",
            boothNo: "बूथ नं :",
            houseNo: "घर नं :",
            pollingLocation: "मतदान स्थान :",
            n_a: "N/A",

            // Action buttons
            call: "कॉल",
            text: "संदेश",
            slip: "स्लिप",
            check: "चेक",
            prachar: "प्रचार",
            print: "प्रिंट",
            family: "परिवार"
        },

        gu: {
            // Page titles
            title: "ઉપનામ શોધ પરિણામો",
            subtitlePrefix: "માટે મતદારો બતાવી રહ્યા છે:",

            // Search
            searchPlaceholder: "પ્રદર્શિત પરિણામોને નામથી ફિલ્ટર કરો...",

            // Messages
            loading: "મતદારો લાવવામાં આવી રહ્યા છે...",
            noVotersFound: "કોઈ મતદાર મળ્યો નથી.",
            noMatchingNames: "કોઈ મેળ ખાતું નામ નથી.",
            mobileNotAvailable: "આ મતદાર માટે મોબાઇલ નંબર ઉપલબ્ધ નથી.",
            fetchFamilyFailed: "કુટુંબ વિગતો મેળવી શકાઈ નહીં.",
            messageError: "સંદેશ બનાવવામાં ભૂલ.",

            // Labels
            fatherHusband: "પિતા/પતિ :",
            address: "સરનામું :",
            serialNumber: "ક્રમાંક :",
            mobile: "મોબાઇલ :",
            idCard: "ઓળખ કાર્ડ :",
            boothNo: "બૂથ નં :",
            houseNo: "ઘર નં :",
            pollingLocation: "મતદાન સ્થળ :",
            n_a: "N/A",

            // Action buttons
            call: "કૉલ",
            text: "સંદેશ",
            slip: "સ્લિપ",
            check: "ચેક",
            prachar: "પ્રચાર",
            print: "પ્રિંટ",
            family: "કુટુંબ"
        }
    };

    const txt = t[lang];

    const surnameListString = location.state?.selectedSurnames || "";

    // --- API Fetch Function ---
    useEffect(() => {
        if (!appId) return;

        if (surnameListString) {
            fetchSurnameVoters();
        } else {
            setLoading(false);
        }
    }, [appId, surnameListString]);

    const fetchSurnameVoters = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/surname_wise_search`,
                {
                    app_id: appId,
                    surname_list: surnameListString
                }
            );

            if (response.data.success) {
                let resultData = response.data.data;

                // --- ROBUST PARSING LOGIC ---
                if (typeof resultData === 'string') {
                    try {
                        resultData = JSON.parse(resultData);
                    } catch (e) {
                        console.warn("Could not parse resultData string:", e);
                    }
                }
                if (resultData && resultData.result) {
                    resultData = resultData.result;
                }
                const votersArray = Array.isArray(resultData) ? resultData : (resultData ? [resultData] : []);

                setVoters(votersArray);
            } else {
                setVoters([]);
            }
        } catch (error) {
            console.error("Error fetching voters:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Client-Side Filter & Pagination Logic ---
    const filteredVoters = voters.filter((voter) => {
        if (!filterName) return true;
        const fName = voter.eng_f_name || "";
        const sName = voter.eng_surname || "";
        const fullName = `${fName} ${sName}`.toLowerCase();
        return fullName.includes(filterName.toLowerCase());
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredVoters.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredVoters.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisibleButtons = 5;
        if (totalPages <= maxVisibleButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) endPage = 4;
            if (currentPage >= totalPages - 2) startPage = totalPages - 3;
            if (startPage > 2) pageNumbers.push('...');
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (endPage < totalPages - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    // --- WhatsApp Message Generation Logic ---
    const handleWhatsAppShare = async (voter) => {
        if (!voter.contact_no) {
            alert(txt.mobileNotAvailable);
            return;
        }
        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_wtsp`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: appId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";
                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('स्री', 'स्त्री')}\n`;
                    message += `${member.idcard_no}\n\n`;
                    message += `मतदान स्थल :\n`;
                    message += `${member.eng_polling_location}\n\n`;
                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });
                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');
                if (mobile.length === 10) mobile = '91' + mobile;
                window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');
            } else {
                alert(txt.fetchFamilyFailed);
            }
        } catch (error) {
            console.error("Error generating WhatsApp slip:", error);
            alert(txt.messageError);
        }
    };

    // Format surnames for display
    const formattedSurnames = surnameListString.split(',').filter(s => s.trim() !== '').join(', ');

    return (
        <div className='app-container p-4'>

            {/* Header Component - Only Title */}
            <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-3">
                <div style={{ flex: 1 }}>
                    <Header title={txt.title} subtitle="" />
                </div>
            </div>

            {/* Subtitle below header with Gradient Line (Option 7) */}
            {formattedSurnames && (
                <div className="subtitle-container mb-4">
                    <p className="subtitle-text">
                        {txt.subtitlePrefix} <strong>{formattedSurnames}</strong>
                    </p>
                </div>
            )}

            {/* --- Professional Search Bar --- */}
            {voters.length > 0 && (
                <div className="filter-container-modern mb-4">
                    <div className="search-wrapper-modern">
                        <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            className="search-input-modern"
                            type="text"
                            placeholder={txt.searchPlaceholder}
                            value={filterName}
                            onChange={(e) => {
                                setFilterName(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loading}</p>
                </div>
            ) : (
                <>
                    <div className="row g-4">
                        {currentItems.length > 0 ? (
                            currentItems.map((voter, index) => (
                                <div className="col-12 col-md-6 col-lg-4" key={index}>
                                    <div className="voter-card-clean h-100">
                                        <div className="voter-header">
                                            <span className="serial-number">{index + 1 + indexOfFirstItem}.</span>
                                            <span className="voter-name-bold ms-2">
                                                {voter.eng_f_name} {voter.eng_surname}
                                            </span>
                                        </div>

                                        <div className="voter-body position-relative">
                                            <div className="map-icon-wrapper">
                                                <img src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg" alt="Map" width="15" />
                                            </div>
                                            <div className="info-row"><span className="label">{txt.fatherHusband}</span><span className="value">{voter.eng_m_name || txt.n_a}</span></div>
                                            <div className="info-row"><span className="label">{txt.address}</span><span className="value">{voter.address || txt.n_a}</span></div>
                                            <div className="info-row"><span className="label">{txt.serialNumber}</span><span className="value">{voter.slnoinpart || voter.serial_no || txt.n_a}</span></div>
                                            <div className="info-row"><span className="label">{txt.mobile}</span><span className="value mobile-value">{voter.contact_no || txt.n_a}</span></div>
                                            <div className="info-row"><span className="label">{txt.idCard}</span><span className="value">{voter.idcard_no || txt.n_a}</span></div>
                                            <div className="info-row"><span className="label">{txt.boothNo}</span><span className="value">{voter.part_no || voter.booth_no || txt.n_a}</span></div>
                                            <div className="info-row"><span className="label">{txt.houseNo}</span><span className="value">{voter.eng_house_no || txt.n_a}</span></div>
                                            <div className="info-row"><span className="label">{txt.pollingLocation}</span><span className="value">{voter.eng_polling_location || "-"}</span></div>
                                        </div>

                                        <div className="voter-actions">
                                            <ActionButton icon="📞" color="#0ea5e9" label={txt.call} link={`tel:${voter.contact_no}`} />
                                            <ActionButton icon="✉️" color="#f59e0b" label={txt.text} link={`sms:${voter.contact_no}`} />
                                            <button onClick={() => handleWhatsAppShare(voter)} className="action-btn-wrapper btn-reset">
                                                <div className="circle-btn" style={{ backgroundColor: '#22c55e' }}>💬</div>
                                                <span className="btn-label">{txt.slip}</span>
                                            </button>
                                            <ActionButton icon="👤" color="#ef4444" label={txt.prachar} />
                                            {/* Updated Print Button with Navigation */}
                                            <ActionButton
                                                icon="🖨"
                                                color="#f97316"
                                                label={txt.print}
                                                onClick={() => navigate('/printer', { state: { voter_id: voter.id } })}
                                            />
                                            <ActionButton icon="👨‍👩‍👧" color="#fbbf24" label={txt.family} />
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center text-muted p-5 w-100">
                                {voters.length === 0 ? txt.noVotersFound : txt.noMatchingNames}
                            </div>
                        )}
                    </div>

                    {/* --- PAGINATION CONTROLS --- */}
                    {filteredVoters.length > itemsPerPage && (
                        <div className="pagination-wrapper">
                            <div className="pagination-content">
                                <button className="pagination-nav-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                                </button>
                                <div className="page-numbers">
                                    {getPageNumbers().map((number, index) => (
                                        <React.Fragment key={index}>
                                            {number === '...' ? <span className="page-dots">...</span> :
                                                <button className={`page-num-btn ${currentPage === number ? 'active' : ''}`} onClick={() => paginate(number)}>{number}</button>}
                                        </React.Fragment>
                                    ))}
                                </div>
                                <button className="pagination-nav-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            <style jsx>{`
                /* Subtitle Styles - Option 7: Gradient Line */
                .subtitle-container {
                    margin-top: 8px;
                    margin-bottom: 20px;
                    position: relative;
                }
                .subtitle-text {
                    font-size: 15px;
                    color: #334155;
                    margin: 0 0 10px 0;
                    font-weight: 600;
                }
                .subtitle-text strong {
                    color: #1e40af;
                    font-weight: 700;
                }
                .subtitle-container::after {
                    content: "";
                    display: block;
                    width: 60px;
                    height: 3px;
                    background: linear-gradient(to right, #2563eb, #60a5fa);
                    border-radius: 3px;
                    margin-top: 4px;
                }

                /* --- SEARCH BAR STYLE --- */
                .filter-container-modern { display: flex; justify-content: flex-start; align-items: center; }
                .search-wrapper-modern { position: relative; width: 100%; max-width: 450px; }
                .search-icon-modern { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); width: 20px; height: 20px; color: #94a3b8; pointer-events: none; z-index: 1; }
                .search-input-modern { width: 100%; padding: 12px 16px 12px 42px; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 14px; color: #1e293b; background-color: #ffffff; outline: none; transition: all 0.2s ease; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .search-input-modern:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }

                @media (max-width: 640px) {
                    .search-wrapper-modern { max-width: 100%; }
                    .subtitle-text {
                        font-size: 14px;
                    }
                    .subtitle-container::after {
                        width: 50px;
                        height: 2px;
                    }
                }

                .btn-reset { background: none; border: none; cursor: pointer; padding: 0; display: flex; flex-direction: column; align-items: center; }
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                .btn-back { background-color: white; color: #64748b; border: 1px solid #e2e8f0; padding: 6px 14px; border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; transition: 0.2s; font-size: 0.9rem; }
                .btn-back:hover { background-color: #f8fafc; color: #1e293b; }
                
                /* Pagination Styles */
                .pagination-wrapper { display: flex; justify-content: center; padding: 30px 0 10px 0; position: sticky; bottom: 20px; z-index: 50; }
                .pagination-content { background: white; border: 1px solid #E2E8F0; border-radius: 50px; padding: 8px 16px; display: flex; align-items: center; gap: 8px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); }
                .pagination-nav-btn { width: 32px; height: 32px; border-radius: 50%; border: none; background: transparent; color: #1e3a8a; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
                .pagination-nav-btn:hover:not(:disabled) { background-color: #eff6ff; }
                .pagination-nav-btn:disabled { color: #cbd5e1; cursor: not-allowed; }
                .page-numbers { display: flex; gap: 4px; align-items: center; }
                .page-num-btn { min-width: 32px; height: 32px; padding: 0 6px; border-radius: 50%; border: 1px solid transparent; background: transparent; color: #64748b; font-weight: 600; font-size: 13px; cursor: pointer; transition: all 0.2s; }
                .page-num-btn:hover { color: #1e3a8a; background-color: #f8fafc; }
                .page-num-btn.active { background-color: #1e3a8a; color: white; box-shadow: 0 2px 4px rgba(30, 58, 138, 0.3); }
                .page-dots { color: #94a3b8; font-weight: bold; padding: 0 4px; }
            `}</style>
        </div>
    );
};
// Updated ActionButton to accept onClick props
const ActionButton = ({ icon, color, label, link, onClick }) => {
    if (onClick) {
        return (
            <button onClick={onClick} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
                <span className="btn-label">{label}</span>
            </button>
        );
    }
    
    return (
        <a href={link || "#"} className="action-btn-wrapper" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">
            <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
            <span className="btn-label">{label}</span>
        </a>
    );
};

export default SurnameWiseVoters;